import { ArrowDownOutlined, ArrowsAltOutlined } from '@ant-design/icons';
import {
  SeasonGroup,
  Species,
  SpeciesSurveyDataV1,
  TechniqueFull,
  WaterbodyDetail,
} from '@omniafishing/core';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import React from 'react';
import { QueryKeys } from '../../../../../constants/query_keys';
import { useWaterbodySummary } from '../../../../../hooks/use_waterbody_summary';
import { apiV1 } from '../../../../../lib/api';
import { FIVE_MINUTES_IN_MS } from '../../../../../lib/time';
import { sqKmToAcres } from '../../../../../lib/waterbody';
import { MuxPlayer, standardPlayerOptionsNoFullscreen } from '../../../../muxPlayer/muxPlayer';
import { OmniaButton } from '../../../../omnia_button/omnia_button';
import SvgAllSpecies from '../../../../svg/all_species_icon';
import { LakeSummary } from '../lake_summary';
import { SpeciesCard } from '../tab_species/species_card';
import { LakeCurrentConditions } from './lake_current_conditions';
import styles from './tab_overview.less';

interface TabOverviewProps {
  waterbody: WaterbodyDetail;
  onTechniqueClick: (techniqueName: string) => void;
  waterbodyTechniques: TechniqueFull[];
  topSpecies: Species;
  topSpeciesSurveyData: SpeciesSurveyDataV1['species'][keyof SpeciesSurveyDataV1['species']];
  onSpeciesClick: (species: Species) => void;
  onAllSpeciesClick: () => void;
  selectedSpecies: Species | null;
  selectedSeasonGroup: SeasonGroup | null;
}

export const TabOverview = (props: TabOverviewProps) => {
  const {
    waterbody,
    onTechniqueClick,
    waterbodyTechniques,
    topSpecies,
    topSpeciesSurveyData,
    onSpeciesClick,
    onAllSpeciesClick,
    selectedSeasonGroup,
    selectedSpecies,
  } = props;
  const { area_sqkm, average_depth_ft, species, species_known } = waterbody;

  const lakeBreakdownVideoQueryParams: Parameters<
    typeof apiV1.waterbodyLakeBreakdownVideoFetch
  >[1] = {
    species: selectedSpecies?.name,
    season_group: selectedSeasonGroup?.name,
  };
  const lakeBreakdownQuery = useQuery({
    queryKey: QueryKeys.WATERBODIES.LAKE_BREAKDOWN_VIDEO(
      waterbody.url_slug,
      lakeBreakdownVideoQueryParams
    ),
    queryFn: async () => {
      const res = await apiV1.waterbodyLakeBreakdownVideoFetch(
        waterbody.url_slug,
        lakeBreakdownVideoQueryParams
      );
      return res.data.data;
    },
    staleTime: FIVE_MINUTES_IN_MS,
  });
  const { data: lakeBreakdownVideo } = lakeBreakdownQuery;

  const { waterbodySummary, hasSummary } = useWaterbodySummary({
    waterbodySlug: waterbody?.url_slug,
  });

  return (
    <div>
      <section className={styles.body}>
        <section className="mb-3">
          <LakeCurrentConditions waterbody={waterbody} />
        </section>

        {hasSummary && (
          <section className="mb-6">
            <LakeSummary
              waterbody={waterbody}
              summary={waterbodySummary}
              selectedSpecies={selectedSpecies}
              selectedSeasonGroup={selectedSeasonGroup}
            />
          </section>
        )}

        {topSpecies && topSpeciesSurveyData && (
          <section className="mb-6 flex flex-col gap-3">
            <p className={classNames(styles.sectionHeader)}>Species Insights</p>
            <SpeciesCard
              species={topSpecies}
              speciesData={topSpeciesSurveyData}
              onClick={onSpeciesClick}
            />
            <OmniaButton kind="tertiary" size="lg" onClick={onAllSpeciesClick} block>
              See All Species
            </OmniaButton>
          </section>
        )}

        <section className="mb-6">
          <p className={classNames(styles.sectionHeader, 'mb-2')}>Top 3 Techniques</p>
          <ol className="flex gap-3 w-[calc(100%+24px)] overflow-auto -ml-3 px-3">
            {waterbodyTechniques.slice(0, 3).map((t) => (
              <li key={t.name} className="w-[36%] sm:w-[31.5%] flex-grow-0 flex-shrink-0">
                <div
                  onClick={() => {
                    onTechniqueClick(t.name);
                  }}
                  className="p-3 rounded-lg border flex flex-col gap-1 items-center text-center font-medium h-full bg-white"
                >
                  <div>
                    <img src={t.image} alt={t.img_alt || ''} width={50} />
                  </div>
                  <span className="mt-auto">{t.display_name}</span>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {lakeBreakdownVideo && (
          <section className={styles.lakeBreakdownSection}>
            <MuxPlayer
              playbackId={lakeBreakdownVideo.mux_playback_id}
              thumbnail={lakeBreakdownVideo.thumbnail}
              className={styles.videoPlayer}
              playerOptions={standardPlayerOptionsNoFullscreen}
            />
          </section>
        )}

        <div className={styles.stats}>
          {area_sqkm && (
            <div className={styles.stat}>
              <div className={styles.statIcon}>
                <ArrowsAltOutlined />
              </div>
              <div className={styles.statText}>
                <span className={styles.statTitle}>Size</span>
                {sqKmToAcres(area_sqkm, 0)} acres
              </div>
            </div>
          )}
          {average_depth_ft && (
            <div className={styles.stat}>
              <div className={styles.statIcon}>
                <ArrowDownOutlined />
              </div>
              <div className={styles.statText}>
                <span className={styles.statTitle}>Avg. Depth</span>
                {Math.round(average_depth_ft)} ft
              </div>
            </div>
          )}
          {species_known && (
            <div className={styles.stat}>
              <div className={styles.statIcon}>
                <SvgAllSpecies />
              </div>
              <div className={styles.statText}>
                <span className={styles.statTitle}>Fish Species</span>
                {species.length}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
